import styled from '@emotion/styled';
import { createRef, Component, useState } from 'react';
import { Provider } from 'react-redux';

import { connectFavorites } from './Favorites/FavoritesConnectors';
import useCrossTabShinglesFavoritesResync from './Favorites/useCrossTabShinglesFavoritesResync';
import Filters from './Filters';
import ColorSamplesCompactPromo from './PrintedColorSamples/CompactPromo';
import { localeCodeHasOrderSamples } from './PrintedColorSamples/availability';
import ShingleList from './ShingleList';
import { useFeatures } from './features';
import filter from './filter';
import { useFiltersConfig } from './filtersConfig';
import withDefaultPrevented from '../../../withDefaultPrevented';
import Conditional from '../../Conditional';
import { withFeatureFlagsProvider } from '../../FeatureFlag';
import FeatureFlagged from '../../FeatureFlag/FeatureFlagged';
import LocaleSpecific from '../../LocaleSpecific';
import Modal from '../../Modal';
import Reusable from '../../OC/PageBuilder/Reusable';
import { H2 } from '../../OC/oc-h';
import Picture from '../../OC/oc-picture';
import Text from '../../OC/oc-text';
import { store } from '../../Store';
import { If, OCNameFilter, PrefixWithLocale } from '../../Utilities';
import { connectLocation } from '../../location/LocationConnectors';
import { FeatureForModal } from '../ShinglesPDP/features';

const FILTER_KEYS = ['colors', 'names', 'prices', 'features', 'windResistances', 'impactResistances', 'warranties'];

export const FeatureModalView = connectLocation(({ feature, locale, onClose }) => {
  if (!feature) {
    return null;
  }

  return feature.reusable?.reference ? (
    <Modal open onClose={onClose} name={feature.title}>
      <h3 className="modal-heading">
        <Text content={feature.title} />
      </h3>

      <div className="features-content reusable">
        <Reusable gutterlessChild language={locale?.code} {...feature?.reusable} />
      </div>
    </Modal>
  ) : (
    <Modal open onClose={onClose} name={feature.heading}>
      <FeatureForModal feature={feature} />
    </Modal>
  );
});
FeatureModalView.WrappedComponent.displayName = 'FeatureModalView';

const IndexView = connectLocation(
  connectFavorites(
    ({
      address,
      appliedFilters,
      colors,
      favorites,
      filters,
      houseView,
      locale,
      onFeatureModalClose,
      onHouseView,
      onResetFilters,
      onShingleView,
      onShowFeature,
      onToggleFilter,
      shingles,
      showFeature,
      openLocationModal,
      zip,
      t,
    }) => {
      useCrossTabShinglesFavoritesResync();
      const onResetFiltersClick = withDefaultPrevented(onResetFilters);
      const [savedZip] = useState(zip);
      const features = useFeatures();
      if (!features) {
        return null;
      }

      const shingleCount = _.reduce(
        filter(features, features, features, features)(shingles, filters),
        (sum, shingle) => sum + _.get(shingle, 'shingle_colors.length', 0),
        0
      );

      const updateBodyOverflow = () => {
        document.body.style.overflow = window.location.hash === '#filters' ? 'hidden' : 'auto';
      };

      window.addEventListener('hashchange', updateBodyOverflow);
      updateBodyOverflow();

      const NoResults = styled.div`
        display: flex;
        width: 860px;
        height: 320px;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        background: #f8f8f8;
        padding: 86px;

        h2 {
          font-size: 32px;
          text-transform: none;
          line-height: 32px;
          font-weight: 700;
          font-family: OCRoboto;
          -webkit-text-stroke-color: #000;
          font-style: normal;
          font-feature-settings: 'clig' off, 'liga' off;
          text-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
        }

        p {
          font-weight: 700;
          font-feature-settings: 'clig' off, 'liga' off;
        }

        a {
          margin-top: 16px;
        }
      `;

      const AppliedFilters = styled.h4`
        padding-right: 24px;
        font-size: 24px;
        font-weight: 700;
        font-family: OCRoboto;
      `;

      const WCAPicture = () => (
        <Picture
          className="wca-image"
          source="https://imagecdn.owenscorning.com/image/upload/f_auto,q_auto/v1742229905/roofing/wca/2025/2025-wca.png"
        />
      );

      return (
        <>
          <If condition={shingleCount >= 0}>
            <div className="bg-full-bleed oc-stickynav">
              <div className="stickynav-container">
                <div className="cta-group">
                  <a
                    href={PrefixWithLocale('/roofing/shingles/favorites', locale.code)}
                    className="oc-cta-button transparent oc-icon favorites"
                    data-track="learn-more"
                    data-track-element-location="main section CTA"
                    data-track-destination="Favorites"
                    aria-label="Favorites"
                  >
                    <i className={`${favorites.length ? 'favorite-filled' : 'favorite'} icon`} aria-hidden="true" />
                    <span>
                      <span role="alert" className="sr-only">{`${
                        !favorites.length ? t('shingles.favorites_no_saved_items') : ''
                      }`}</span>
                      {t('shingles.favorites')}
                    </span>
                    &nbsp;({favorites.length})
                  </a>

                  <FeatureFlagged except={['contractor_referral']}>
                    <a
                      href={PrefixWithLocale('/roofing/contractors', locale.code)}
                      className="oc-cta-button"
                      data-track="find-a-professional"
                      data-track-professional="contractor"
                    >
                      {t('shingles.find_contractor')}
                    </a>
                  </FeatureFlagged>

                  <div className="filter-tab-container tablet-only">
                    <a href="#filters" className="filter-trigger oc-icon">
                      <i className="icon filters" />
                      {t('shingles.filters.header')}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <section name="results">
              <div className="heading-container">
                <H2 role="status">
                  {shingleCount} {t('shingles.roofing_shingles')}
                  {address.city ? ` ${t('shingles.near')} ${address.city}, ${address.region}` : null}
                  <button
                    className="change-zip oc-icon"
                    data-tm="change-zip-header-cta"
                    data-track="open-modal"
                    data-track-modal-name="Region Selector"
                    href="#"
                    onClick={openLocationModal}
                  >
                    <i className="icon location" aria-hidden="true" />
                    {t('shingles.change_zip')}
                  </button>
                </H2>

                <div className="tab-toggle" role="radiogroup" aria-label="Choose your view">
                  <button
                    role="radio"
                    data-track="view-change"
                    className={`${houseView ? 'active' : null} tab house`}
                    data-track-view="House View"
                    onClick={onHouseView}
                    aria-checked={houseView}
                  >
                    {t('shingles.house_view')}
                  </button>

                  <button
                    role="radio"
                    data-track="view-change"
                    className={`${houseView ? null : 'active'} tab shingles`}
                    data-track-view="Shingle View"
                    onClick={onShingleView}
                    aria-checked={!houseView}
                  >
                    {t('shingles.shingle_view')}
                  </button>
                </div>
              </div>

              <div className="filter-tab-container tablet-mobile-only">
                <div className="tab-toggle">
                  <button
                    role="radio"
                    data-track="view-change"
                    className={`${houseView ? 'active' : null} tab house`}
                    data-track-view="House View"
                    onClick={onHouseView}
                    aria-checked={houseView}
                  >
                    {t('shingles.house_view')}
                  </button>

                  <button
                    role="radio"
                    data-track="view-change"
                    className={`${houseView ? null : 'active'} tab shingles`}
                    data-track-view="Shingle View"
                    onClick={onShingleView}
                    aria-checked={!houseView}
                  >
                    {t('shingles.shingle_view')}
                  </button>
                </div>
              </div>

              <div className="filter-tab-container phone-only">
                <a href="#filters" className="filter-trigger oc-icon">
                  <i className="icon filters" />
                  {t('shingles.filters.header')}
                </a>
                <Conditional if={appliedFilters.length}>
                  <button
                    className="oc-icon reset-filter small-screen"
                    onClick={onResetFiltersClick}
                    data-track="reset"
                  >
                    <i className="icon reset" aria-hidden="true" />
                    {t('shingles.filters.reset')}
                  </button>
                </Conditional>
              </div>

              <div className="columns ratio-1-3">
                <section role="complementary" aria-label="Filters Sidebar" id="filters" className="col filters-column">
                  <a className="skip-to-search-results" href="#searchresults">
                    {t('shingles.filters.skip_to_results')}
                  </a>
                  <div className="filter-sidebar-header">
                    <H2 className="mobile-heading tablet-mobile-only">
                      {shingleCount} {t('shingles.roofing_shingles')}
                    </H2>
                    <div className="filter-close-container">
                      <a href="#!" className="filter-close">
                        &times;
                      </a>
                    </div>
                  </div>

                  <Conditional if={appliedFilters.length}>
                    <div className="filter-sidebar-row">
                      <button
                        className="oc-icon reset-filter filters-sidebar"
                        onClick={onResetFiltersClick}
                        data-track="reset"
                      >
                        <i className="icon reset" aria-hidden="true" />
                        {t('shingles.filters.reset')}
                      </button>
                      <div className="applied-filters-container">
                        <AppliedFiltersList filters={appliedFilters} onToggleFilter={onToggleFilter} />
                      </div>
                    </div>
                  </Conditional>

                  <Filters
                    filters={filters}
                    onToggleFilter={onToggleFilter}
                    onShowFeature={onShowFeature}
                    shingles={shingles}
                    colors={colors}
                  />

                  <Conditional if={localeCodeHasOrderSamples(locale.code)}>
                    <ColorSamplesCompactPromo />
                  </Conditional>
                  <LocaleSpecific only={['en-US', 'es-US']}>
                    <div className="wca-container">
                      <WCAPicture />
                      <h4>{t('shingles.statics.wca.header')}</h4>
                      <p>{t('shingles.statics.wca.body')}</p>
                      <a href={t('shingles.statics.wca.cta_link')}>{t('shingles.statics.wca.cta_text')}</a>
                    </div>
                  </LocaleSpecific>

                  <div className="filter-control tablet-mobile-only">
                    <a className="control" href="#" onClick={onResetFiltersClick}>
                      {t('shingles.filters.reset')}
                    </a>

                    <a href="#!" className="control apply-filter">
                      {t('shingles.filters.apply')}
                      <br />({shingleCount})
                    </a>
                  </div>
                </section>

                <section id="searchresults" aria-label={t('shingles.search_results')} className="col shingle-results">
                  <Conditional if={appliedFilters.length}>
                    <div className="applied-filters-container">
                      <AppliedFilters>{t('shingles.noResult.appliedFilters')}: </AppliedFilters>
                      <AppliedFiltersList filters={appliedFilters} onToggleFilter={onToggleFilter} />
                      <button
                        className="oc-icon reset-filter large-screen"
                        onClick={onResetFiltersClick}
                        data-track="reset"
                      >
                        <i className="icon reset" aria-hidden="true" />
                        {t('shingles.filters.reset')}
                      </button>
                    </div>
                  </Conditional>

                  {shingleCount > 0 ? (
                    <>
                      <ShingleList
                        locale={locale}
                        shingles={filter(features, features, features, features)(shingles, filters)}
                        onShowFeature={onShowFeature}
                        houseView={houseView}
                        address={address}
                      />

                      <LocaleSpecific only={['en-US', 'es-US']}>
                        <div className="wca-banner">
                          <WCAPicture />
                          <div className="content">
                            <h4>{t('shingles.statics.wca_banner.heading')}</h4>
                            <p>{t('shingles.statics.wca_banner.copy')}</p>
                          </div>
                        </div>
                      </LocaleSpecific>
                    </>
                  ) : (
                    <NoResults>
                      <div>
                        <h2>{t('shingles.noResult.heading')}</h2>
                        <p>{t('shingles.noResult.desc')}</p>

                        <LocaleSpecific>
                          <a
                            className="oc-cta-button"
                            onClick={onResetFiltersClick}
                            data-track="find-a-professional"
                            data-track-professional="reset"
                          >
                            {t('shingles.noResult.cta')}
                          </a>
                        </LocaleSpecific>
                      </div>
                    </NoResults>
                  )}
                </section>
              </div>
            </section>

            <Reusable language={locale.code} name="Color Disclaimer" path="roofing" />
          </If>

          <FeatureModalView feature={showFeature} onClose={onFeatureModalClose} />
        </>
      );
    }
  )
);
IndexView.WrappedComponent.displayName = 'IndexView';

const AppliedFilterView = ({ filterKey, value, onToggleFilter }) => {
  const onRemoveFilterClick = withDefaultPrevented(() => onToggleFilter({ [filterKey]: value }));
  const filtersConfig = useFiltersConfig();
  if (!filtersConfig) {
    return null;
  }
  const { analytics, label } = filtersConfig[filterKey];

  return (
    <button
      className="applied-filter"
      tabIndex="0"
      onClick={onRemoveFilterClick}
      data-track="remove-filter"
      data-track-filter-name={analytics.name}
      data-track-filter-option={analytics.option(value)}
    >
      <OCNameFilter>{label(value)}</OCNameFilter>

      <span className="close" aria-hidden="true">
        &times;
      </span>
    </button>
  );
};

const AppliedFiltersList = ({ filters, onToggleFilter }) =>
  filters.flatMap(([key, values]) =>
    values.map(value => (
      <AppliedFilterView key={[key, value]} filterKey={key} value={value} onToggleFilter={onToggleFilter} />
    ))
  );

class List extends Component {
  constructor(props) {
    super(props);

    this.thisPage = createRef();
    const params = new URLSearchParams(location.search);
    const getParam = key => {
      const param = params.get(key);

      if (param && param.length) {
        return param.split(',');
      }

      return [];
    };

    const filters = {};
    FILTER_KEYS.forEach(key => (filters[key] = getParam(key)));

    this.state = {
      houseView: false,
      showFeature: null,
      filters,
    };
  }

  toggleFilter(filter) {
    this.setState(
      state => {
        const filters = { ...state.filters };

        Object.entries(filter).forEach(([key, value]) => {
          let current = filters[key];
          const idx = current.indexOf(value);

          if (idx >= 0) {
            current = current.filter(v => v !== value);
          } else {
            current = [...current, value];
          }

          filters[key] = current;
        });

        return { filters };
      },
      () => this.updateQueryParams()
    );
    window.scrollTo(0, 0);
  }

  updateQueryParams() {
    const query = this.appliedFilters.flatMap(([key, values]) => `${key}=${values.join(',')}`);

    let path = window.location.pathname;
    if (query.length) {
      path += `?${query.join('&')}`;
    }

    history.replaceState('shingles', document.title, path);
  }

  get appliedFilters() {
    return Object.entries(this.state.filters).filter(([_, values]) => values.length);
  }

  resetFilters() {
    const filters = {};
    FILTER_KEYS.forEach(key => (filters[key] = []));

    this.setState({ filters }, () => this.updateQueryParams());
  }

  render() {
    const props = {
      appliedFilters: this.appliedFilters,
      colors: _.uniq(this.props.shingles.flatMap(shingle => shingle.shingle_colors).flatMap(col => col.colors)),
      ...this.props,
      ...this.state,
      onFeatureModalClose: () => this.setState({ showFeature: null }),
      onHouseView: () => this.setState({ houseView: true }),
      onResetFilters: () => this.resetFilters(),
      onShingleView: () => this.setState({ houseView: false }),
      onShowFeature: showFeature => this.setState({ showFeature }),
      onToggleFilter: filter => this.toggleFilter(filter),
    };

    return (
      <Provider store={store}>
        <IndexView {...props} />
      </Provider>
    );
  }
}
const PYSFilters = withFeatureFlagsProvider(List);
PYSFilters.displayName = 'PYSFilters';
export default PYSFilters;
